import React, { useEffect, useRef, useState } from 'react';
import { Platform, StyleSheet, Text, TextInput, View } from 'react-native';

import OTPVoiceCallContainer from './OTPVoiceCallContainer';

import {
  PinCodeDispatchMethod,
  Platform as ReCaptchaPlatform,
} from 'apollo/queries/autogenerate/schemas-public';
import useInitiateAuth from 'shared/modules/auth/hooks/use-initiate-auth';
import useValidatePin from 'shared/modules/auth/hooks/use-validate-pin';
import CodeInput from 'shared/modules/common/components/atoms/CodeInput';

type SessionCredentials = {
  email: string;
  phone: string;
  sessionToken: string;
};

type Props = {
  /**
   * Invoked when component is mounted. The code field is auto-focused at this point.
   */
  onFocus?: () => void;
  /**
   * Invoked when an error has occurred during voice call invocation or OTP submission
   */
  onError?: (err: Error) => void;
  /**
   * Invoked when one-time password is valid
   */
  onSuccess?: () => void;
  /**
   * The valid email, phone, and sessionToken combination returned by Signin
   */
  sessionCredentials: SessionCredentials;
  /**
   * WEB | IOS | ANDROID
   */
  reCaptchaPlatform: ReCaptchaPlatform;
  /**
   *  Every signin attempt requires a new ReCaptcha token. This function should return a Promise with the ReCaptcha token as the resolved value.
   */
  getRecaptchaTokenFunction: () => Promise<string>;
};

const PinCode = (props: Props) => {
  const initiateAuth = useInitiateAuth();
  const validatePin = useValidatePin();

  /**
   * Session token changes for each failed OTP attempt
   */
  const [sessionToken, setSessionToken] = useState(
    props.sessionCredentials.sessionToken
  );

  const [showError, setShowError] = useState(false);

  const formRef = useRef<View>(null);
  const codeInputRef = useRef<TextInput>(null);

  useEffect(() => {
    // Automatically focus the input
    codeInputRef.current?.focus();
    props.onFocus?.();

    const htmlForm = (formRef.current as unknown) as HTMLFormElement;

    /**
     * If this is rendered on the Web, OTP autofill tells the form to submit.
     * But a form submission causes the browser to reload, so we don't want that default behavior.
     * Instead, this should be passed to the props.onSuccess callback.
     *
     * Q: Why don't we just remove the <form />? Just use <div />.
     * A: Because accessibility. And many features do not work correctly if we are not semantic. So please be semantic!
     */
    const dontSubmit = (event: Event) => {
      event.stopPropagation();
      event.preventDefault();
    };

    htmlForm?.addEventListener('submit', dontSubmit);

    return () => {
      htmlForm?.removeEventListener('submit', dontSubmit);
    };
  }, []);

  const onVoiceCallInvoke = async () => {
    const reCaptchaToken = await props.getRecaptchaTokenFunction();

    try {
      const newSessionToken = await initiateAuth({
        email: props.sessionCredentials.email,
        phone: props.sessionCredentials.phone,
        method: PinCodeDispatchMethod.VOICE,
        reCaptchaPlatform: props.reCaptchaPlatform,
        reCaptchaToken,
      });

      setSessionToken(newSessionToken);
    } catch (err) {
      props.onError?.(err as Error);
    }
  };

  const onOTPFulfill = async (code: string) => {
    setShowError(false);

    try {
      await validatePin({ code, sessionToken });

      props.onSuccess?.();
    } catch (err) {
      setShowError(true);

      /**
       * Data returned by catch is untypeable,
       * so all we can do is use TypeScript's 'as' to assert the data type
       *
       * If the result has a sessionToken key then it must be a new OTP attempt
       */
      if ((err as any).sessionToken) {
        setSessionToken((err as any).sessionToken as string);
      } else {
        props.onError?.(err as Error);
      }
    }
  };

  return (
    <View>
      <View
        style={style.root}
        {...Platform.select({
          web: { accessibilityRole: 'form', ref: formRef },
        })}
      >
        <View>
          <Text style={[style.text, style.title]}>MyPaidyにログイン</Text>
          <Text style={[style.text, { fontSize: 14 }]}>
            携帯電話に届いた認証コードを入力して下さい。
          </Text>
          <View style={style.spacer} />
          <CodeInput
            onFulFill={onOTPFulfill}
            showError={showError}
            errorMessage="認証コードに誤りがあります"
            style={style.codeField}
            accessibilityLabel="Enter 4 digit PIN code"
            id="pin-code-field"
            ref={codeInputRef}
          />
        </View>
      </View>
      <OTPVoiceCallContainer onVoiceCallInvoke={onVoiceCallInvoke} />
    </View>
  );
};

const style = StyleSheet.create({
  root: {
    flex: 1,
    justifyContent: 'space-between',
  },
  text: {
    marginBottom: 8,
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
  },
  codeField: {
    width: 272,
    alignSelf: 'center',
  },
  spacer: {
    marginBottom: 48,
  },
});

export default PinCode;
